import { LockOutlined } from "@ant-design/icons";
import { Tag, Tooltip } from "antd";
import { ReactNode, useMemo } from "react";
import DOCUMENT_VISIBILITY from "../../../../../../../types/enums/DocumentVisibility";
import { ReactComponent as PersonPrimary } from "../../../../../../../images/icons/PersonPrimaryVisibility.svg";
import { ReactComponent as PersonSecondary } from "../../../../../../../images/icons/PersonSecondaryVisibility.svg";

type PortalVisibilityLabelProps = {
  val: DOCUMENT_VISIBILITY[];
  tooltipLabel: string;
};

export const PortalVisibilityLabel = ({
  val,
  tooltipLabel,
}: PortalVisibilityLabelProps) => {
  val = val.sort((a, b) => a - b) as DOCUMENT_VISIBILITY[]; // Ensure the order of visibility types is consistent

  const visibilityIcons: Record<DOCUMENT_VISIBILITY, ReactNode> = useMemo(
    () => ({
      [DOCUMENT_VISIBILITY.None]: <LockOutlined />,
      [DOCUMENT_VISIBILITY.PrimarySeller]: (
        <span role="img" aria-label="lock" className="anticon anticon-lock">
          {<PersonPrimary />}
        </span>
      ),
      [DOCUMENT_VISIBILITY.PrimaryBuyer]: (
        <span role="img" aria-label="lock" className="anticon anticon-lock">
          {<PersonPrimary />}
        </span>
      ),
      [DOCUMENT_VISIBILITY.SecondarySeller]: (
        <span role="img" aria-label="lock" className="anticon anticon-lock">
          {<PersonSecondary />}
        </span>
      ),
      [DOCUMENT_VISIBILITY.SecondaryBuyer]: (
        <span role="img" aria-label="lock" className="anticon anticon-lock">
          {<PersonSecondary />}
        </span>
      ),
    }),
    [],
  );

  const visibilityLabels: Record<DOCUMENT_VISIBILITY, string> = useMemo(
    () => ({
      [DOCUMENT_VISIBILITY.None]: "not visible in the portal",
      [DOCUMENT_VISIBILITY.PrimarySeller]: "primary seller",
      [DOCUMENT_VISIBILITY.PrimaryBuyer]: "primary buyer",
      [DOCUMENT_VISIBILITY.SecondarySeller]: "secondary seller",
      [DOCUMENT_VISIBILITY.SecondaryBuyer]: "secondary buyer",
    }),
    [],
  );

  const visibilityTagLabels: Record<DOCUMENT_VISIBILITY, string> = useMemo(
    () => ({
      [DOCUMENT_VISIBILITY.None]: "not visible",
      [DOCUMENT_VISIBILITY.PrimarySeller]: "Seller",
      [DOCUMENT_VISIBILITY.PrimaryBuyer]: "Buyer",
      [DOCUMENT_VISIBILITY.SecondarySeller]: "Seller",
      [DOCUMENT_VISIBILITY.SecondaryBuyer]: "Buyer",
    }),
    [],
  );

  const primaryVisibilityTypes = useMemo(
    () => [DOCUMENT_VISIBILITY.PrimarySeller, DOCUMENT_VISIBILITY.PrimaryBuyer],
    [],
  );

  const tooltipTitle = useMemo(
    () =>
      val.includes(DOCUMENT_VISIBILITY.None)
        ? `The ${tooltipLabel} is ${visibilityLabels[DOCUMENT_VISIBILITY.None]}`
        : `The ${tooltipLabel} is visible to ${val
            .map((visibility) => visibilityLabels[visibility])
            .join(", ")}`,
    [val, tooltipLabel, visibilityLabels],
  );

  if (val.includes(DOCUMENT_VISIBILITY.None)) {
    return (
      <Tooltip placement="top" overlayClassName="text-sm" title={tooltipTitle}>
        {visibilityIcons[DOCUMENT_VISIBILITY.None]}
      </Tooltip>
    );
  }

  return (
    <Tooltip placement="top" overlayClassName="text-sm" title={tooltipTitle}>
      <>
        {val.length === 4 ? (
          <Tag>All</Tag>
        ) : (
          val.map((visibility, index) => (
            <Tag
              key={visibility}
              icon={visibilityIcons[visibility]}
              color={
                primaryVisibilityTypes.includes(visibility) ? "blue" : undefined
              }
              className={index < val.length - 1 ? "mr-1" : "mr-0"}
            >
              {visibilityTagLabels[visibility]}
            </Tag>
          ))
        )}
        {}
      </>
    </Tooltip>
  );
};
